<script setup lang="ts">
import { CheckCircle2, CircleX, FileText, Info, Upload } from 'lucide-vue-next';
import { computed, ref } from 'vue';

import type { DemandFormModel } from './types.ts';

import PassAPIConnector from './api';
import VCheckbox from './subcomponents/VCheckbox.vue';
import VInput from './subcomponents/VInput.vue';
import VInputFile from './subcomponents/VInputFile.vue';
import VSelect from './subcomponents/VSelect.vue';

const props = defineProps<{ apiKey: string }>();

const TYPES_JUSTIFICATIFS = [
  { value: 'UTILITY_BILL', label: 'Facture de téléphone (y compris mobile)' },
  { value: 'UTILITY_BILL', label: 'Facture d\'électricité ou de gaz' },
  { value: 'UTILITY_BILL', label: 'Facture d\'eau' },
  { value: 'RENT_RECEIPT', label: 'Quittance de loyer ou titre de propriété' },
  { value: 'TAX_NOTICE', label: 'Avis d\'imposition ou certificat de non-imposition' },
  { value: 'HOUSING_CERTIFICATE', label: 'Attestation ou facture d\'assurance du logement' },
  { value: 'CAF_STATEMENT', label: 'Relevé de la Caf (aides au logement)' },
];

const initialFormData: DemandFormModel = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  street: '',
  city: '',
  zipCode: '',
  documentType: null,
  file: null,
  filename: '',
  rgpdAgreement: false,
  cguAccepted: false,
};

const formData = ref<DemandFormModel>(JSON.parse(JSON.stringify(initialFormData)));
const fileInputKey = ref<number>(0);
const apiHandler = new PassAPIConnector(props.apiKey);

const submitted = ref(false);
const submitting = ref(false);
const fileError = ref('');
const submitError = ref(false);
const phoneError = ref(false);
const zipError = ref(false);
const serverErrorMessage = ref('');

function resetForm() {
  formData.value = JSON.parse(JSON.stringify(initialFormData));
  submitted.value = false;
  submitting.value = false;
  submitError.value = false;
  phoneError.value = false;
  zipError.value = false;
  fileError.value = '';
  fileInputKey.value++;
  serverErrorMessage.value = '';
}

function handleFileChange(file: File | null) {
  fileError.value = '';
  formData.value.file = null;
  if (file) {
    const maxSize = 5 * 1024 * 1024; // 5MB en bytes
    if (file.size > maxSize) {
      fileError.value = 'Le fichier ne doit pas dépasser 5 MB';
      return;
    }
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      fileError.value = 'Format non accepté. Veuillez utiliser PDF, JPEG ou PNG';
      return;
    }
    formData.value.file = file;
    formData.value.filename = file.name;
  }
}

const isFormValid = computed(() => {
  return (
    formData.value.firstname
      && formData.value.lastname
      && formData.value.email
      && formData.value.phone
      && formData.value.street
      && formData.value.city
      && formData.value.zipCode
      && formData.value.documentType
      && formData.value.file
      && formData.value.filename
      && formData.value.rgpdAgreement
      && formData.value.cguAccepted
  );
});

const fileInfo = computed(() => {
  if (formData.value.file && !fileError.value) {
    return `Fichier sélectionné : ${formData.value.file.name} (${(formData.value.file.size / 1024).toFixed(0)} KB)`;
  }
  return null;
});

const isFileUploadedAndValid = computed(() => {
  return !!formData.value.file && !fileError.value;
});

async function handleSubmit(e: Event) {
  e.preventDefault();

  if (!isFormValid.value)
    return;

  submitting.value = true;
  submitError.value = false;
  serverErrorMessage.value = '';

  const dataToSubmit: DemandFormModel = { ...formData.value };

  let payload;
  if (formData.value.file && formData.value.filename && formData.value.documentType) {
    payload = apiHandler.getNewPassPayload(dataToSubmit);
    if (payload) {
      const response = await apiHandler.postNewPassRequest(payload);
      if (response.success) {
        submitting.value = false;
        submitted.value = true;
      }
      else {
        submitting.value = false;
        submitError.value = true;
        if (response.code === 400 && response.error?.includes('already exists')) {
          serverErrorMessage.value = 'Une demande existe déjà pour ce nom, prénom ou email.';
        }
        else {
          serverErrorMessage.value = 'Une erreur est survenue lors de l\'envoi.';
        }
      }
    }
  }
}

function isValidPhoneNumber(phoneNumber: string) {
  const phoneRegex: RegExp = /^\d{10}$/;
  const test = phoneRegex.test(phoneNumber);
  if (test) {
    submitError.value = false;
    phoneError.value = false;
  }
  else {
    submitError.value = true;
    phoneError.value = true;
  }
  formData.value.phone = phoneNumber;
}

function isValidZipCode(zip: string) {
  const phoneRegex: RegExp = /^\d{5}$/;
  const test = phoneRegex.test(zip);
  if (test) {
    submitError.value = false;
    zipError.value = false;
  }
  else {
    submitError.value = true;
    zipError.value = true;
  }
  formData.value.zipCode = zip;
}
</script>

<template>
  <main class="card-container">
    <header class="card-header">
      <div class="header-content">
        <div class="icon-wrapper">
          <FileText class="icon-main" />
        </div>
        <div>
          <h1 class="card-title">
            Demande de Pass Marseille
          </h1>
          <p class="card-description">
            Veuillez remplir tous les champs pour soumettre votre demande
          </p>
        </div>
      </div>
    </header>

    <div class="card-content">
      <div class="alert info-alert">
        <Info class="alert-icon info-icon" />
        <div class="alert-description info-text">
          <div class="info-content">
            <p><strong>Justificatifs de domicile acceptés :</strong></p>
            <ul class="justificatifs-list">
              <li>• Facture de téléphone (y compris de téléphone mobile)</li>
              <li>• Facture d'électricité ou de gaz</li>
              <li>• Quittance de loyer (d'un organisme social ou d'une agence immobilière) ou titre de propriété</li>
              <li>• Facture d'eau</li>
              <li>• Avis d'imposition ou certificat de non-imposition</li>
              <li>• Attestation ou facture d'assurance du logement</li>
              <li>• Relevé de la Caf mentionnant les aides liées au logement</li>
            </ul>
          </div>
        </div>
      </div>

      <form class="form-container" @submit="handleSubmit">
        <div class="grid-2-cols">
          <VInput
            id="prenom"
            :model-value="formData.firstname"
            type="text"
            label="Prénom"
            placeholder="Votre prénom"
            :required="true"
            @update:model-value="(v) => formData.firstname = v"
          />
          <VInput
            id="nom"
            :model-value="formData.lastname"
            type="text"
            label="Nom"
            placeholder="Votre nom"
            :required="true"
            @update:model-value="(v) => formData.lastname = v"
          />
        </div>

        <VInput
          id="email"
          :model-value="formData.email"
          label="Email"
          type="email"
          placeholder="votre.email@exemple.fr"
          :required="true"
          @update:model-value="(v) => formData.email = v"
        />

        <VInput
          id="tel"
          :model-value="formData.phone"
          label="Téléphone"
          type="phone"
          placeholder="06 12 34 56 78"
          :required="true"
          :error="phoneError ? 'Numéro de téléphone invalide' : null"
          @update:model-value="(v) => isValidPhoneNumber(v)"
        />

        <VInput
          id="adresse"
          v-model="formData.street"
          label="Adresse"
          type="text"
          placeholder="Numéro et nom de rue"
          :required="true"
          @update:model-value="(v) => formData.street = v"
        />

        <div class="grid-2-cols">
          <VInput
            id="codePostal"
            :model-value="formData.zipCode"
            type="text"
            label="Code postal"
            placeholder="13000"
            :required="true"
            :error="zipError ? 'Code postal invalide' : null"
            @update:model-value="(v) => isValidZipCode(v)"
          />
          <VInput
            id="ville"
            :model-value="formData.city"
            type="text"
            label="Ville"
            placeholder="Ville"
            :required="true"
            @update:model-value="(v) => formData.city = v"
          />
        </div>

        <VSelect
          id="typeJustificatif"
          :model-value="formData.documentType"
          label="Type de justificatif"
          placeholder="Sélectionnez le type de justificatif"
          :options="TYPES_JUSTIFICATIFS"
          :required="true"
          @update:model-value="(v) => formData.documentType = v"
        />

        <div class="file-input-group-rgpd">
          <div class="file-flex">
            <VInputFile
              id="fichier"
              :key="fileInputKey"
              :model-value="formData.file"
              label="Justificatif de domicile"
              accept=".pdf,.jpg,.jpeg,.png"
              :required="true"
              class="custom-file-input"
              @update:model-value="handleFileChange"
            />
            <div v-if="isFileUploadedAndValid" class="file-status-icon">
              <CheckCircle2 class="icon-check" />
            </div>
          </div>
          <p class="rgpd-info-text">
            Pour garantir la protection de vos données personnelles, nous vous invitons à masquer les informations non
            nécessaires sur votre justificatif avant de l’envoyer. Ce document sera conservé pendant une durée d’un mois
            maximum avant d’être définitivement supprimé.
          </p>
          <p v-if="fileError" class="file-error-text">
            {{ fileError }}
          </p>
          <p v-else-if="fileInfo" class="file-info-text-selected">
            {{ fileInfo }}
          </p>
          <p v-else class="file-info-text">
            Formats acceptés : PDF, JPEG, PNG (max. 5 MB)
          </p>
        </div>

        <VCheckbox
          id="consentementRgpd"
          :model-value="formData.rgpdAgreement"
          label="J'accepte que mes données personnelles soient collectées et traitées dans le cadre de ma demande de Pass Marseille, conformément a notre politique de confidentialité."
          link="https://www.marseille-tourisme.com/politique-de-confidentialite/"
          class="checkbox-container"
          :required="true"
          @update:model-value="(v) => formData.rgpdAgreement = v"
        />

        <VCheckbox
          id="conditionsGenerales"
          :model-value="formData.cguAccepted"
          label=" J'accepte les conditions générales d'utilisation du Pass Marseille."
          class="checkbox-container"
          :required="true"
          @update:model-value="(v) => formData.cguAccepted = v"
        />

        <button
          type="submit"
          :disabled="!isFormValid || submitted || submitting"
          class="submit-button"
          :class="[{ 'submit-disabled': !isFormValid || submitted || submitError }, { 'submit-error': submitError }]"
        >
          <template v-if="submitted">
            <CheckCircle2 class="button-icon" />
            Demande envoyée
          </template>
          <template v-if="submitError && !serverErrorMessage">
            <CircleX class="button-icon" />
            Une erreur est survenue.
          </template>
          <template v-else-if="submitError && serverErrorMessage">
            <CircleX class="button-icon" />
            {{ serverErrorMessage }}
          </template>
          <template v-else-if="!submitted">
            <Upload class="button-icon" />
            Soumettre la demande
          </template>
        </button>
      </form>

      <div v-if="submitted" class="alert success-alert">
        <div class="success-alert-wrapper">
          <CheckCircle2 class="alert-icon success-icon" />
          <p class="alert-description success-text">
            Votre demande a été soumise avec succès ! Vous recevrez un email de confirmation.
          </p>
        </div>
      </div>

      <button
        v-if="submitted || serverErrorMessage" type="button" class="reset-button" style="marginTop:1rem;"
        @click="resetForm"
      >
        Faire une nouvelle demande
      </button>
    </div>
  </main>
</template>

<style scoped>
.card-container {
  font-family: var(--yb-default-font), serif;
  width: 100%;
  max-width: 48rem;
  margin: 0 auto;
  background-color: var(--yb-color-white);
  border-radius: var(--yb-radius-sm);
  box-shadow:
    0 4px 8px -2px rgba(0, 0, 0, 0.1),
    0 2px 4px 0 rgba(0, 0, 0, 0.05);
  border: 1px solid var(--yb-color-primary);
}

.card-header {
  padding: 1.5rem;
  margin-block: 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  @media screen and (width < 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.icon-wrapper {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--yb-color-primary);
  padding: 0.75rem;
  border-radius: 9999px;
}

.icon-main {
  width: 1.5rem;
  height: 1.5rem;
  color: var(--yb-color-black);
}

.card-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--yb-color-black);
  margin-top: 0;
  margin-bottom: 0.25rem;
}

.card-description {
  font-size: 0.875rem;
  color: #888383;
  margin-block: 0;
}

.card-content {
  padding: 1rem;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.grid-2-cols {
  display: grid;
  gap: 1.5rem;
}

@media (min-width: 640px) {
  .grid-2-cols {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.required-star {
  color: var(--yb-color-error);
}

.alert {
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 0.375rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.alert-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.alert-description {
  font-size: 0.875rem;
  line-height: 1.5;
}

.success-alert {
  display: flex;
  align-items: center;
  margin-top: 1.5rem;
  border: 1px solid #a7f3d0;
  background-color: #f0fdf4;
}

.success-alert-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
}

.success-icon {
  color: #059669;
}

.success-text {
  color: #065f46;
}

.info-alert {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  border: 1px solid var(--yb-color-primary);
  background-color: var(--yb-color-light-primary);
}

.info-icon {
  color: var(--yb-color-primary);
  padding-right: 0.5rem;
}

.info-text {
  color: var(--yb-color-dark-primary);
}

.info-content {
  line-height: 1.5;

  p {
    margin-top: 0;
  }
}

.info-content strong {
  font-weight: 600;
}

.justificatifs-list {
  font-size: 0.875rem;
  margin-top: var(--yb-spacing-xs);
  list-style: none;
  padding-inline-start: 0;
}

.justificatifs-list li {
  margin-bottom: 0.25rem;
}

.file-input-group {
  display: flex;
  flex-direction: column;
  gap: var(--yb-radius-sm);
}

.file-input-group-rgpd {
  display: flex;
  flex-direction: column;
}

.file-flex {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.file-status-icon {
  display: flex;
  align-items: center;
  gap: var(--yb-spacing-xs);
  background-color: white;
  color: #059669;
  position: absolute;
  top: 23px;
  right: -8px;
}

.icon-check {
  width: 1.25rem;
  height: 1.25rem;
}

.file-error-text {
  font-size: 0.875rem;
  color: var(--yb-color-error);
}

.file-info-text {
  font-size: 0.875rem;
  color: var(--yb-color-black);
  margin-block: 0;
}

.rgpd-info-text {
  font-size: 0.875rem;
  color: var(--yb-color-black);
  line-height: 130%;
  margin-block: 0.875rem;
}

.file-info-text-selected {
  font-size: 0.875rem;
  color: var(--yb-color-black);
}

.checkbox-container {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: var(--yb-radius-sm);
  border: 1px solid var(--yb-color-lightgray);
}

.checkbox-container label {
  font-size: 0.875rem;
  cursor: pointer;
  line-height: 1.5;
  flex: 1;
}

.optional {
  background-color: var(--yb-color-light-primary);
  border-color: var(--yb-color-primary);
}

.submit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem 1.5rem;
  background-color: var(--yb-color-light-primary);
  color: var(--yb-color-black);
  border: none;
  border-radius: var(--yb-radius-sm);
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.reset-button {
  font-family: var(--yb-default-font), serif;
  font-size: 0.875rem;
  display: block;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem 1.5rem;
  background-color: #f0fdf4;
  color: #065f46;
  border: 1px solid #a7f3d0;
  border-radius: var(--yb-radius-sm);
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    border: 1px solid #065f46;
  }
}

.submit-error {
  background-color: var(--yb-color-lightgray);
  color: var(--yb-color-error);
}

.submit-button:disabled {
  background-color: var(--yb-color-lightgray);
  cursor: not-allowed;
  opacity: 0.7;
}

.button-icon {
  width: 1rem;
  height: 1rem;
  margin-right: var(--yb-spacing-xs);
}
</style>
